import React, { useEffect, useState } from "react";
import { BankForm } from "./BankForm";
import { BankCard } from "./BankCard";
import { bankService, BankResponse } from "../service/bankService";
import { Button } from "@components/shadcn-ui/button";
import { useToast } from "@customHooks/useToast";
import { Plus, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@components/shadcn-ui/dialog";
import { useMultiStepForm } from "features/loanApplications/context/MultiStepFormContext";

interface BankManagerProps {
  applicationId: string;
  leadId: string;
  onBankAccountsUpdated?: (bankIds: string[]) => void;
}

export const BankManager: React.FC<BankManagerProps> = ({
  applicationId,
  leadId,
  onBankAccountsUpdated,
}) => {
  const [banks, setBanks] = useState<BankResponse[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [currentEditBank, setCurrentEditBank] = useState<BankResponse | null>(null);
  const { toast } = useToast();
  const { setBankIds } = useMultiStepForm();

  // Debug log to verify props
  console.log("BankManager props:", { applicationId, leadId });

  useEffect(() => {
    if (applicationId) {
      fetchBanks();
    } else {
      setIsLoading(false);
    }
  }, [applicationId]);

  const fetchBanks = async () => {
    setIsLoading(true);
    try {
      const response = await bankService.getBanksByApplicationId(applicationId);
      setBanks(response.bankAccounts || []);
      
      // Update bank IDs in context
      const bankIds = response.bankAccounts.map(bank => bank.id);
      setBankIds(bankIds);
      
      // Also call the prop callback if provided
      if (onBankAccountsUpdated) {
        onBankAccountsUpdated(bankIds);
      }
    } catch (error) {
      console.error("Error fetching banks:", error);
      toast({
        variant: "destructive",
        description: "Failed to load bank accounts",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleBankAdded = (newBank: BankResponse) => {
    setBanks(prev => [...prev, newBank]);
    setShowAddForm(false);
    
    // Update bank IDs in context
    const bankIds = [...banks, newBank].map(bank => bank.id);
    setBankIds(bankIds);
    
    // Also call the prop callback if provided
    if (onBankAccountsUpdated) {
      onBankAccountsUpdated(bankIds);
    }
  };

  const handleDeleteBank = async (bankId: string) => {
    if (window.confirm("Are you sure you want to delete this bank account?")) {
      try {
        await bankService.deleteBank(bankId);
        
        // Update local state
        const updatedBanks = banks.filter(bank => bank.id !== bankId);
        setBanks(updatedBanks);
        
        // Update bank IDs in context
        const bankIds = updatedBanks.map(bank => bank.id);
        setBankIds(bankIds);
        
        // Also call the prop callback if provided
        if (onBankAccountsUpdated) {
          onBankAccountsUpdated(bankIds);
        }
        
        toast({
          description: "Bank account deleted successfully",
        });
      } catch (error) {
        console.error("Error deleting bank account:", error);
        toast({
          variant: "destructive",
          description: "Failed to delete bank account",
        });
      }
    }
  };

  const handleEditBank = (bank: BankResponse) => {
    setCurrentEditBank(bank);
    setIsEditDialogOpen(true);
  };

  const handleUpdateBank = (updatedBank: BankResponse) => {
    if (!currentEditBank) return;
    
    try {
      // Update local state
      const updatedBanks = banks.map(bank => 
        bank.id === updatedBank.id ? updatedBank : bank
      );
      setBanks(updatedBanks);
      
      // Update bank IDs in context
      const bankIds = updatedBanks.map(bank => bank.id);
      setBankIds(bankIds);
      
      // Also call the prop callback if provided
      if (onBankAccountsUpdated) {
        onBankAccountsUpdated(bankIds);
      }
      
      setIsEditDialogOpen(false);
      setCurrentEditBank(null);
    } catch (error) {
      console.error("Error updating bank account:", error);
      toast({
        variant: "destructive",
        description: "Failed to update bank account",
      });
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center p-8">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <span className="ml-2">Loading bank accounts...</span>
      </div>
    );
  }

  return (
    <div className="w-full">
      <div className="flex justify-between items-center mb-4 mt-4">
        <h2 className="text-lg font-semibold">Bank Account Details</h2>
        <Button 
          onClick={() => setShowAddForm(true)}
          size="sm"
          className="flex items-center gap-1"
        >
          <Plus className="h-4 w-4" /> Add Bank Account
        </Button>
      </div>

      {showAddForm && (
        <BankForm
          applicationId={applicationId}
          leadId={leadId}
          onBankAdded={handleBankAdded}
          onCancel={() => setShowAddForm(false)}
        />
      )}

      {banks.length === 0 && !showAddForm ? (
        <div className="text-center p-8 border border-dashed rounded-md">
          <p className="text-gray-500">No bank accounts added yet</p>
          <Button 
            onClick={() => setShowAddForm(true)}
            variant="outline"
            className="mt-2"
          >
            Add your first bank account
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {banks.map((bank) => (
            <BankCard
              key={bank.id}
              bank={bank}
              onDelete={handleDeleteBank}
              onEdit={handleEditBank}
            />
          ))}
        </div>
      )}

      {/* Edit Bank Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-hidden">
          <DialogHeader className="sticky top-0 bg-white z-10 pb-2">
            <DialogTitle>Edit Bank Account</DialogTitle>
          </DialogHeader>
          {currentEditBank && (
            <BankForm
              applicationId={applicationId}
              leadId={leadId}
              onBankAdded={handleUpdateBank}
              onCancel={() => setIsEditDialogOpen(false)}
              initialData={currentEditBank}
              isEditMode={true}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
